import axios, { AxiosError } from "axios";
import format from "date-fns/format";
import { useQuery } from "react-query";
import { API_URL } from "../consts";
import { Role, oraPrenotaStore } from "../oraPrenotaStore";
import { Loading } from "../ui/Loading";
import { changeDatesToUTC, readDate } from "../utils/dateUtils";
import { EuroFormatter } from "../utils/EuroFormatter";
import { useMutation } from "react-query";
import { useSnackbar } from "notistack";
import { queryClient } from "../App";
import { DatePicker } from "@mui/x-date-pickers";
import { memo, useCallback, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import { Anagrafiche, Cards, Prenotazioni } from "../prismaTypes";

export function ViewPrenotazione(props: { id: number; handleClose: () => void }) {
  const { id: idN, handleClose } = props;
  const role = oraPrenotaStore((state) => state.role);
  const { enqueueSnackbar } = useSnackbar();

  const id = idN ?? -1;

  const { isLoading, data } = useQuery<Prenotazioni, AxiosError>(
    ["prenotazioni/get", id],
    () => axios.get(API_URL + "prenotazioni/get/" + id).then((res) => res.data),
    {
      onSuccess(data) {
        setDataInizio(data ? readDate(data.dataInizioPrenotazione) : null);
      },
    },
  );

  const { isLoading: isLoadingP, data: par } = useQuery<
    { partecipanti: Anagrafiche[] },
    AxiosError
  >(
    ["prenotazioni/getPartecipanti", id],
    () => axios.get(API_URL + "prenotazioni/getPartecipanti/" + id).then((res) => res.data),
    {},
  );

  const { isLoading: isLoading2, data: card } = useQuery<
    {
      cards: (Cards & {
        idMembro: number;
        nome: string;
        cognome: string;
        importoResiduo: number;
        dataScadenzaCertificato: string;
        dataScadenza: string;
        prenotazioneMultipla: number;
      })[];
    },
    AxiosError
  >(["cards/findMyCard"], () => axios.get(API_URL + "cards/findMyCard").then((res) => res.data), {
    enabled: role === "USER",
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const [dataInizio, setDataInizio] = useState<Date | null>(
    data ? readDate(data.dataInizioPrenotazione) : null,
  );
  const [dataFine, setDataFine] = useState<Date | null>(null);
  const [causale, setCausale] = useState("");

  const [lunedi, setLunedi] = useState(true);
  const [martedi, setMartedi] = useState(true);
  const [mercoledi, setMercoledi] = useState(true);
  const [giovedi, setGiovedi] = useState(true);
  const [venerdi, setVenerdi] = useState(true);
  const [sabato, setSabato] = useState(true);
  const [domenica, setDomenica] = useState(true);

  const { mutate: annulla } = useMutation(
    (data: {}) => {
      return axios.post(API_URL + "prenotazioni/annulla/" + id, {});
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Prenotazione annullata", {
          variant: "success",
        });
        handleClose();
      },
      onError: (error: AxiosError) => {
        queryClient.invalidateQueries();
        enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    },
  );

  const { mutate: annullaSerie } = useMutation(
    (data1: {}) => {
      return axios.post(API_URL + "prenotazioni/annullaSerie/" + data?.prenotazioneParentId, {});
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Prenotazione annullata", {
          variant: "success",
        });
        handleClose();
      },
      onError: (error: AxiosError) => {
        queryClient.invalidateQueries();
        enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    },
  );

  const { mutate: ripeti } = useMutation(
    (data: any) => {
      return axios.post(API_URL + "prenotazioni/ripeti/", data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Prenotazione ripetuta", { variant: "success" });
        handleClose();
      },
      onError: (error: AxiosError) => {
        queryClient.invalidateQueries();
        enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    },
  );

  const annullaClick = useCallback(() => annulla({}), [annulla]);
  const annullaSerieClick = useCallback(() => annullaSerie({}), [annullaSerie]);

  if (!data) return <></>;
  if (isLoading || isLoading2 || isLoadingP) return <Loading />;

  const utenteSuper = role === Role.ADMIN || role === Role.CLUB_ADMIN || role === Role.BACKOFFICE;
  const annullabileSi = data.annullabile === 1;

  const isPrenotante =
    data.isPrenotante ||
    data.cardsId === card?.cards[0].id ||
    data.membersId === card?.cards[0].idMembro;

  const ripetibile =
    role !== "USER" ||
    (card?.cards[0].prenotazioneMultipla === 1 &&
      (data.cardsId === card?.cards[0].id || data.membersId === card?.cards[0].idMembro));

  const ripetiClick = () => {
    const data = {
      idPrenotazione: id,
      dataInizio,
      dataFine,
      causale,
      lunedi,
      martedi,
      mercoledi,
      giovedi,
      venerdi,
      sabato,
      domenica,
    };
    changeDatesToUTC(data);

    if (dataFine === null || dataInizio === null) {
      enqueueSnackbar("È necessario specificare data inizio e data fine", {
        variant: "error",
      });
      return;
    }
    ripeti(data);
  };

  let nomeCognome = ((data?.cognomeAna ?? "") + " " + (data?.nomeAna ?? "")).trim();
  if (nomeCognome === "") nomeCognome = "Amministratore";

  const len = (par?.partecipanti.length ?? 0) - 1;

  let partecipanti = "";
  if (par)
    par.partecipanti.forEach((part, index) => {
      partecipanti += part.nome + " " + part.cognome + (index !== len ? ", " : "");
    });

  return (
    <>
      <DialogTitle>
        {data.causale === "" || data.causale === null ? "Prenotazione" : data.causale}
      </DialogTitle>
      <DialogContent>
        <Stack gap={1}>
          <Sintesi
            dataInizioPrenotazione={data.dataInizioPrenotazione}
            dataFinePrenotazione={data.dataFinePrenotazione}
            denominazione={data.denominazione}
          />
          <br />
          Partecipanti: {partecipanti}
          <TabellaCosti
            totalePerGiocatori={data.totalePerGiocatori}
            totalePerFacilities={data.totalePerFacilities}
            cifraAddebitata={data.cifraAddebitata}
          />
          {data.ricevuta}
          {utenteSuper && (
            <>
              Prenotazione numero {Math.floor(data.id)}
              <br />
              Effettuata da {nomeCognome}
              <br />
              Card addebitata: {data.nomeCard} {data.cognomeCard}
              <br />
              Il {format(new Date(data?.dataInserimento), "dd/MM/yyyy HH:mm")}
            </>
          )}
          {utenteSuper ||
            (role !== "USER" && data?.prenotazioneParentId !== null && (
              <>
                <br />
                Serie: {data?.prenotazioneParentId}
              </>
            ))}
          {!utenteSuper && isPrenotante && !annullabileSi && (
            <Alert severity="warning">Termini per l'annullamento scaduti</Alert>
          )}
          {(utenteSuper || (annullabileSi && role === "USER" && isPrenotante)) && (
            <Button color="error" variant="contained" onClick={() => annullaClick()}>
              Annulla Prenotazione
            </Button>
          )}
          {data?.prenotazioneParentId !== null &&
            (utenteSuper || (ripetibile && annullabileSi && data?.membro === 1)) && (
              <Button color="error" variant="contained" onClick={() => annullaSerieClick()}>
                Annulla Serie
              </Button>
            )}
        </Stack>

        {utenteSuper && (
          <Stack gap={1} mt={1}>
            {data.note && (
              <div>
                <Typography variant="h6">Note</Typography>
                <div>{data.note}</div>
              </div>
            )}
          </Stack>
        )}

        {ripetibile && (
          <Stack gap={1} mt={1}>
            <div>
              <Typography variant="h6">Ripeti prenotazione</Typography>
              <DatePicker
                label="Dal"
                value={dataInizio}
                onChange={(newValue) => {
                  setDataInizio(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    sx={{ width: "150px", mr: 2 }}
                    variant="standard"
                  />
                )}
              />
              <DatePicker
                label="Al"
                value={dataFine}
                onChange={(newValue) => {
                  setDataFine(newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} required sx={{ width: "150px" }} variant="standard" />
                )}
              />
            </div>
            <div>
              <FormControlLabel
                label="Lunedì"
                control={
                  <Checkbox checked={lunedi} onChange={(v) => setLunedi(v.target.checked)} />
                }
              />

              <FormControlLabel
                label="Martedì"
                control={
                  <Checkbox checked={martedi} onChange={(v) => setMartedi(v.target.checked)} />
                }
              />

              <FormControlLabel
                label="Mercoledì"
                control={
                  <Checkbox checked={mercoledi} onChange={(v) => setMercoledi(v.target.checked)} />
                }
              />
            </div>
            <div>
              <FormControlLabel
                label="Giovedì"
                control={
                  <Checkbox checked={giovedi} onChange={(v) => setGiovedi(v.target.checked)} />
                }
              />

              <FormControlLabel
                label="Venerdì"
                control={
                  <Checkbox checked={venerdi} onChange={(v) => setVenerdi(v.target.checked)} />
                }
              />

              <FormControlLabel
                label="Sabato"
                control={
                  <Checkbox checked={sabato} onChange={(v) => setSabato(v.target.checked)} />
                }
              />

              <FormControlLabel
                label="Domenica"
                control={
                  <Checkbox checked={domenica} onChange={(v) => setDomenica(v.target.checked)} />
                }
              />
            </div>
            <TextField
              label="Causale ripetizione"
              fullWidth
              variant="standard"
              value={causale}
              onChange={(e) => setCausale(e.target.value)}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {ripetibile && (
          <Button onClick={ripetiClick} autoFocus>
            Ripeti
          </Button>
        )}
        <Button onClick={handleClose} autoFocus>
          Chiudi
        </Button>
      </DialogActions>
    </>
  );
}

const TabellaCosti = memo(function TabellaCosti(props: {
  totalePerGiocatori: number;
  totalePerFacilities: number;
  cifraAddebitata: number;
}) {
  const { totalePerGiocatori, totalePerFacilities, cifraAddebitata } = props;
  return (
    <Table size="small" sx={{ maxWidth: "350px" }}>
      <TableBody>
        <TableRow>
          <TableCell>Totale Soci</TableCell>
          <TableCell align="right" sx={{ minWidth: "8em" }}>
            {EuroFormatter.format(totalePerGiocatori)}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Totale Facilities</TableCell>
          <TableCell align="right" sx={{ minWidth: "8em" }}>
            {EuroFormatter.format(totalePerFacilities)}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell sx={{ fontWeight: "600" }}>Totale</TableCell>
          <TableCell align="right" sx={{ minWidth: "8em", fontWeight: "600" }}>
            {EuroFormatter.format(cifraAddebitata)}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
});

const Sintesi = memo(function Sintesi(props: {
  dataInizioPrenotazione: string;
  dataFinePrenotazione: string;
  denominazione: string;
}) {
  const { dataInizioPrenotazione, dataFinePrenotazione, denominazione } = props;
  return (
    <>
      Prenotazione per il giorno {format(readDate(dataInizioPrenotazione), "dd/MM/yyyy")}
      <br />
      Dalle {format(readDate(dataInizioPrenotazione), "HH:mm")} alle{" "}
      {format(readDate(dataFinePrenotazione), "HH:mm")}
      <br />
      Impianto: {denominazione}
    </>
  );
});
